import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { ChannelMark } from './ChannelIcons';
import { startConversation, type ChannelView } from './api';

/**
 * Writing to somebody who has not written first (context.md §45–§56). Phase 72.
 *
 * The inbox could only ever answer. Everything in it began with a customer message, so a business
 * that wanted to *start* a conversation — a quote asked for on the phone, a delivery update, a
 * follow-up — opened WhatsApp on a handset instead, which is the habit this product exists to
 * replace, and nothing they sent then appeared in the CRM.
 *
 * The channel is chosen first and on purpose. It decides what an address even means and whether a
 * free-form first message is allowed at all, so asking for it before the number is the difference
 * between a refusal now and a message that fails an hour later in a delivery log.
 */
export function NewConversationDialog({
  channels,
  onClose,
  onStarted,
}: {
  channels: readonly ChannelView[];
  onClose: () => void;
  onStarted: (conversationId: string) => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const client = useQueryClient();

  /*
   * Only channels that can actually open a conversation.
   *
   * The official WhatsApp route needs an approved template to write first — Meta's rule, not ours
   * — so offering it here would be offering a message the server refuses. It is left out rather
   * than shown disabled, because a disabled option with no explanation is a puzzle.
   */
  const usable = channels.filter(
    (channel) => channel.status !== 'DISABLED' && channel.provider !== 'whatsapp_cloud',
  );

  const [channelId, setChannelId] = useState(usable[0]?.id ?? '');
  const [to, setTo] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = dialog.current;

    if (element && !element.open) element.showModal();
  }, []);

  const start = useMutation({
    mutationFn: () => startConversation({ channelId, to: to.trim(), body: body.trim() }),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: ['inbox'] });
      onStarted(result.conversationId);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const incomplete = channelId === '' || to.trim().length < 3 || body.trim() === '';

  return (
    <dialog
      ref={dialog}
      className="confirm confirm--wide"
      aria-label={t('inbox.newConversation')}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();

          if (!incomplete) start.mutate();
        }}
      >
        <h2>{t('inbox.newConversation')}</h2>

        {usable.length === 0 ? (
          <p className="muted">{t('inbox.newConversationNoChannel')}</p>
        ) : (
          <>
            <label className="stack__field">
              <span>{t('inbox.newConversationChannel')}</span>
              <select
                className="select"
                value={channelId}
                onChange={(event) => setChannelId(event.target.value)}
              >
                {usable.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.name}
                  </option>
                ))}
              </select>
              <span className="muted small cell-inline">
                {usable.find((channel) => channel.id === channelId)?.kind && (
                  <ChannelMark kind={usable.find((channel) => channel.id === channelId)!.kind} />
                )}
                {t('inbox.newConversationChannelHint')}
              </span>
            </label>

            <label className="stack__field">
              <span>
                {t('inbox.newConversationTo')} <span className="required">*</span>
              </span>
              <input
                value={to}
                maxLength={254}
                autoFocus
                placeholder="+55 21 98765-4321"
                onChange={(event) => setTo(event.target.value)}
              />
              <span className="muted small">{t('inbox.newConversationToHint')}</span>
            </label>

            <label className="stack__field">
              <span>
                {t('inbox.newConversationBody')} <span className="required">*</span>
              </span>
              <textarea
                rows={4}
                value={body}
                maxLength={4000}
                placeholder={t('inbox.newConversationBodyPlaceholder')}
                onChange={(event) => setBody(event.target.value)}
              />
            </label>
          </>
        )}

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="confirm__actions">
          <button type="button" className="button button--quiet button--compact" onClick={onClose}>
            {t('confirm.cancel')}
          </button>

          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={incomplete || start.isPending || usable.length === 0}
          >
            {start.isPending ? t('inbox.sending') : t('inbox.send')}
          </button>
        </div>
      </form>
    </dialog>
  );
}
